import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { EsimNumber } from "../../models/Number";
import {
  fetchNumbers as fetchNumbersApi,
  createNumber as createNumberApi,
  updateNumber as updateNumberApi,
  deleteNumber as deleteNumberApi,
} from "../../services/apiConfig";
import type { ListParams } from "../../services/api";

interface NumbersState {
  numbers: EsimNumber[];
  /** The current page only - the server no longer sends the whole table. */
  total: number;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: NumbersState = {
  numbers: [],
  total: 0,
  loading: "idle",
  error: null,
};

export const fetchNumbers = createAsyncThunk(
  "numbers/fetchNumbers",
  async (params?: ListParams) => {
    const response = await fetchNumbersApi(params);
    return response;
  },
);

export const addNumber = createAsyncThunk(
  "numbers/addNumber",
  async (
    number: Omit<
      EsimNumber,
      "id" | "created" | "expiration_date" | "auto_renew"
    >,
  ) => {
    const response = await createNumberApi(number);
    return response;
  },
);

export const editNumber = createAsyncThunk(
  "numbers/editNumber",
  async (number: EsimNumber) => {
    const response = await updateNumberApi(number.id, number);
    return response;
  },
);

export const removeNumber = createAsyncThunk(
  "numbers/removeNumber",
  async (id: number) => {
    await deleteNumberApi(id);
    return id;
  },
);

const numbersSlice = createSlice({
  name: "numbers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNumbers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchNumbers.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.numbers = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(fetchNumbers.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch numbers";
      })
      .addCase(addNumber.fulfilled, (state, action) => {
        state.numbers.push(action.payload);
      })
      .addCase(editNumber.fulfilled, (state, action) => {
        const index = state.numbers.findIndex(
          (num) => num.id === action.payload.id,
        );
        if (index !== -1) {
          state.numbers[index] = action.payload;
        }
      })
      .addCase(removeNumber.fulfilled, (state, action) => {
        state.numbers = state.numbers.filter(
          (num) => num.id !== action.payload,
        );
      });
  },
});

export default numbersSlice.reducer;
